import React from 'react';
import { Helmet } from 'react-helmet-async';

// Canonical/OG URLs must be absolute for Google and social networks
// (Facebook/LinkedIn/Twitter ignore relative og:url and og:image).
const SITE_URL = 'https://thinkingworld.online';
const DEFAULT_IMAGE = `${SITE_URL}/img/company-logo.png`;

// Default keyword set targeting India website-development & conversion search intent.
// Pages can override/extend via the `keywords` prop.
const DEFAULT_KEYWORDS = [
  'THINKINGWORLD',
  'Web2Sales',
  'best website developers in India',
  'best website development company India',
  'top website designers in India',
  'website design company India',
  'professional website developers India',
  'ecommerce website developers India',
  'custom website development India',
  'website development agency India',
  'website makers in India',
  'best web design agency India',
  'website development cost in India',
  'website design cost in India',
  'ecommerce website cost India',
  'business website cost India',
  'small business website cost India',
  'Shopify website cost India',
  'WordPress website cost India',
  'cost to build website in India',
  'website pricing India 2026',
  'affordable website development India',
  'conversion focused website design',
  'CRO website design India',
  'high converting ecommerce website',
  'website that increases sales',
  'conversion optimization services India',
  'UX focused website design',
  'performance driven website design',
  'sales driven website development',
  'landing page optimization India',
  'conversion rate optimization agency India',
  'D2C website development India',
  'ecommerce website for brands India',
  'Shopify development for D2C brands',
  'website for clothing brand India',
  'ecommerce UX optimization India',
  'product page optimization India',
  'checkout optimization India',
  'improve ecommerce conversion rate India',
  'website redesign for ecommerce',
  'ecommerce performance marketing website',
  'why my website is not converting',
  'high bounce rate website fix',
  'low ecommerce conversion rate',
  'website not generating sales',
  'why customers leave website',
  'how to improve website conversion rate',
  'website UX issues',
  'ecommerce store not making profit',
  'why ads not converting website problem',
  'landing page not converting',
  'website that generates leads',
  'sales driven website design India',
  'ROI focused website development',
  'website for lead generation India',
  'growth focused website agency',
  'performance website design India',
  'website optimization services India',
  'scale ecommerce with better website',
  'website audit for conversion',
  'UX audit India',
  'how much does a website cost in India',
  'why website development is expensive',
  'how to choose website development company India',
  'affordable website for small business India',
  'website vs marketplace which is better',
  'why D2C brands need websites',
  'website mistakes killing conversions',
  'how to improve ecommerce UX',
  'best platform for ecommerce website India',
  'Shopify vs WordPress India',
  'website developers in Bangalore',
  'website designers in Mumbai',
  'ecommerce developers Delhi',
  'website development company Hyderabad',
  'website developers in India',
  'sales driven websites',
  'websites that sell',
  'build website that converts',
  'conversion first website agency',
  'website to sales system',
  'performance websites India',
].join(', ');

const toAbsolute = (value, fallback) => {
  if (!value) return fallback;
  if (/^https?:\/\//i.test(value)) return value;
  return `${SITE_URL}${value.startsWith('/') ? '' : '/'}${value}`;
};

const SeoHead = ({
  title,
  description,
  image,
  path = '/',
  noindex = false,
  keywords,
}) => {
  const fullTitle = title
    ? `${title} | THINKINGWORLD`
    : 'THINKINGWORLD — Web, Mobile, IoT & Digital Marketing';
  const desc =
    description ||
    'THINKINGWORLD builds websites, mobile apps, IoT platforms and digital marketing engines for healthcare, real estate, ecommerce, nutrition and education businesses.';
  const url = toAbsolute(path, SITE_URL);
  const ogImage = toAbsolute(image, DEFAULT_IMAGE);
  const keywordsContent = keywords
    ? `${keywords}, ${DEFAULT_KEYWORDS}`
    : DEFAULT_KEYWORDS;

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'THINKINGWORLD',
    alternateName: 'Web2Sales',
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    description:
      'Sales-driven, conversion-focused website development agency in India. We build high-converting ecommerce, D2C and business websites that turn traffic into revenue.',
    areaServed: 'IN',
    knowsAbout: [
      'website development India',
      'ecommerce website development',
      'conversion rate optimization',
      'D2C website development',
      'Shopify development',
      'UX design',
    ],
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={keywordsContent} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow" />}
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      <link rel="canonical" href={url} />
    </Helmet>
  );
};

export default SeoHead;
